<!-- <template>
  <div
    :class="[
      'searchable-select',
      {
        'with-action': !!$slots.action,
        'is-open': open,
        'is-disabled': disabled,
      },
    ]"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <input
      ref="inputEl"
      class="search-input"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      v-model="query"
      role="combobox"
      :aria-expanded="String(open)"
      :aria-controls="listboxId"
      :aria-activedescendant="activeId || null"
      aria-autocomplete="list"
      autocomplete="off"
      @keydown="onKeydown"
      @input="onInput"
      @focus="openList"
    />

    <button
      v-if="$slots.action"
      type="button"
      class="combo-action-btn"
      :disabled="disabled"
      @mousedown.prevent
      @click="$emit('action')"
      aria-label="azione"
    >
      <slot name="action" />
    </button>

    <ul
      v-show="open"
      ref="listEl"
      class="search-results"
      :id="listboxId"
      role="listbox"
      @mousedown.prevent
      @touchstart.prevent
    >
      <li
        v-for="(opt, idx) in items"
        :key="valueKey(opt, idx)"
        class="combo-option"
        :class="{ 'is-active': idx === activeIndex }"
        role="option"
        :id="optionId(idx)"
        :aria-selected="String(idx === activeIndex)"
        @mouseenter="setActive(idx)"
        @mousemove="setActive(idx)"
        @click="choose(idx)"
        @touchend.prevent="choose(idx)"
      >
        <slot name="option" :option="opt" :label="labelKey(opt)" />
        <template v-if="!$slots.option">{{ labelKey(opt) }}</template>
      </li>

      <li
        v-if="!loading && items.length === 0"
        class="combo-empty"
        aria-disabled="true"
      >
        {{ emptyText }}
      </li>
      <li v-if="loading" class="combo-loading" aria-disabled="true">
        {{ loadingText }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SearchableCombo",
  props: {
    modelValue: [String, Number, Object, null],
    items: { type: Array, default: () => [] },
    labelKey: {
      type: Function,
      default: (o) => o?.label ?? o?.name ?? o?.title ?? "",
    },
    valueKey: {
      type: Function,
      default: (o) => o?.id ?? o?.value ?? JSON.stringify(o),
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    emptyText: { type: String, default: "Nessun risultato" },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "Caricamento…" },
    initialQuery: { type: String, default: "" },
  },
  emits: [
    "update:modelValue",
    "search",
    "select",
    "action",
    "query-change",
    "cleared",
  ],
  data() {
    return {
      open: false,
      query: this.initialQuery,
      activeIndex: -1,
      listboxId: `lb-${Math.random().toString(36).slice(2, 8)}`,
    };
  },
  computed: {
    activeId() {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    },
  },
  watch: {
    initialQuery(q) {
      this.query = q || "";
    },
    // 👇 quando il parent azzera v-model, svuoto input e chiudo lista
    modelValue(v) {
      if (v === null || v === "" || typeof v === "undefined") {
        this.query = "";
        this.closeList();
        this.$emit("cleared");
      }
    },
  },
  mounted() {
    const st = window.getComputedStyle(this.$refs.inputEl);
    if (parseFloat(st.fontSize) < 16)
      this.$refs.inputEl.style.fontSize = "16px"; // iOS zoom fix
  },
  methods: {
    clear() {
      this.query = "";
      this.closeList();
      this.$emit("update:modelValue", null);
      this.$emit("cleared");
    },
    optionId(i) {
      return `${this.listboxId}-opt-${i}`;
    },
    openList() {
      if (this.disabled) return;
      this.open = true;
      this.$nextTick(this.ensureVisible);
    },
    closeList() {
      this.open = false;
      this.activeIndex = -1;
    },
    onFocusIn() {},
    onFocusOut(e) {
      const goingTo = e.relatedTarget;
      if (!goingTo || !this.$el.contains(goingTo)) this.closeList();
    },
    onInput() {
      this.$emit("query-change", this.query);
      this.$emit("search", this.query?.trim() || "");
      this.openList();
      this.activeIndex = this.items.length ? 0 : -1;
    },
    onKeydown(e) {
      if (!this.open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        this.openList();
        e.preventDefault();
        this.activeIndex = this.items.length ? 0 : -1;
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          if (this.items.length) {
            this.activeIndex = (this.activeIndex + 1) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "ArrowUp":
          if (this.items.length) {
            this.activeIndex =
              (this.activeIndex - 1 + this.items.length) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "Enter":
          if (this.open && this.activeIndex >= 0) {
            this.choose(this.activeIndex);
            e.preventDefault();
          }
          break;
        case "Escape":
          this.closeList();
          e.preventDefault();
          break;
      }
    },
    setActive(i) {
      this.activeIndex = i;
    },
    choose(i) {
      const opt = this.items[i];
      if (!opt) return;
      const val = this.valueKey(opt, i);
      this.$emit("update:modelValue", val);
      this.$emit("select", opt);
      this.query = this.labelKey(opt);
      this.closeList();
      this.$nextTick(() => this.$refs.inputEl?.focus());
    },
    ensureVisible() {
      const list = this.$refs.listEl;
      if (!list || this.activeIndex < 0) return;
      const el = list.children[this.activeIndex];
      if (!el) return;
      const top = el.offsetTop,
        bottom = top + el.offsetHeight;
      const viewTop = list.scrollTop,
        viewBottom = viewTop + list.clientHeight;
      if (top < viewTop) list.scrollTop = top;
      else if (bottom > viewBottom) list.scrollTop = bottom - list.clientHeight;
    },
  },
};
</script> -->

<template>
  <div
    :class="[
      'searchable-select',
      {
        'with-action': !!$slots.action,
        'is-open': open,
        'is-disabled': disabled,
      },
    ]"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <input
      ref="inputEl"
      class="search-input"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      v-model="query"
      role="combobox"
      :aria-expanded="String(open)"
      :aria-controls="listboxId"
      :aria-activedescendant="activeId || null"
      aria-autocomplete="list"
      autocomplete="off"
      @keydown="onKeydown"
      @input="onInput"
      @focus="openList"
    />

    <!-- [sc-48337] Azzera il campo. `clear()` esisteva già ma non aveva un
         comando: si poteva svuotare solo cancellando a mano. Sta prima del
         pulsante azione perché è più vicino al testo che annulla. -->
    <button
      v-if="query && !disabled"
      type="button"
      class="combo-clear-btn"
      @mousedown.prevent
      @click="clear"
      title="Azzera la ricerca"
      aria-label="Azzera la ricerca"
    >
      <i class="fa-solid fa-times"></i>
    </button>

    <button
      v-if="$slots.action"
      type="button"
      class="combo-action-btn"
      :disabled="disabled"
      @mousedown.prevent
      @click="$emit('action')"
      aria-label="azione"
    >
      <slot name="action" />
    </button>

    <ul
      v-show="open"
      ref="listEl"
      class="search-results"
      :id="listboxId"
      role="listbox"
      @mousedown.prevent
      @touchstart.prevent
    >
      <li
        v-for="(opt, idx) in items"
        :key="valueKey(opt, idx)"
        class="combo-option"
        :class="{ 'is-active': idx === activeIndex }"
        role="option"
        :id="optionId(idx)"
        :aria-selected="String(idx === activeIndex)"
        @mouseenter="setActive(idx)"
        @mousemove="setActive(idx)"
        @click="choose(idx)"
        @touchend.prevent="choose(idx)"
      >
        <slot name="option" :option="opt" :label="labelKey(opt)" />
        <template v-if="!$slots.option">{{ labelKey(opt) }}</template>
      </li>

      <li
        v-if="!loading && items.length === 0"
        class="combo-empty"
        aria-disabled="true"
      >
        {{ emptyText }}
      </li>
      <li v-if="loading" class="combo-loading" aria-disabled="true">
        {{ loadingText }}
      </li>

      <li
        v-if="showPagination && totalPages > 1"
        class="combo-pagination"
        role="presentation"
      >
        <div class="combo-pagination__info">
          Pagina {{ currentPage }} di {{ totalPagesLabel }}
          <template v-if="totalItems != null">
            · {{ totalItemsLabel }} risultati</template
          >
          <template v-if="requestedPage != null"> · caricamento…</template>
        </div>
        <div class="combo-pagination__controls">
          <button
            type="button"
            class="combo-page-btn"
            :disabled="activePage === 1"
            @click="goToPage(1)"
          >
            Inizio
          </button>
          <button
            type="button"
            class="combo-page-btn"
            :disabled="activePage === 1"
            @click="goToPage(activePage - 1)"
          >
            Precedente
          </button>

          <template v-for="p in visiblePages">
            <span v-if="p.type === 'gap'" :key="p.key" class="combo-page-gap"
              >…</span
            >
            <button
              v-else
              :key="p.key"
              type="button"
              class="combo-page-btn combo-page-num"
              :class="{
                'is-current': p.value === currentPage,
                'is-pending': p.value === requestedPage,
              }"
              :aria-current="p.value === currentPage ? 'page' : null"
              :disabled="p.value === currentPage || p.value === requestedPage"
              @click="goToPage(p.value)"
            >
              {{ p.value }}
            </button>
          </template>

          <button
            type="button"
            class="combo-page-btn"
            :disabled="activePage === totalPages"
            @click="goToPage(activePage + 1)"
          >
            Successivo
          </button>
          <button
            type="button"
            class="combo-page-btn"
            :disabled="activePage === totalPages"
            @click="goToPage(totalPages)"
          >
            Ultimo
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SearchableCombo",
  props: {
    modelValue: [String, Number, Object, null],
    /** 👇 nuovo: testo controllato dal parent */
    text: { type: String, default: "" },
    /** Paginazione opzionale e retrocompatibile dei risultati di ricerca:
     * senza queste prop il footer non compare e il componente si comporta
     * esattamente come prima. I numeri di pagina derivano solo dalla risposta
     * API (currentPage/totalPages/totalItems). */
    currentPage: { type: Number, default: 1 },
    /** Pagina della richiesta più recente in volo (pending), o null se nessuna.
     * Opzionale e retrocompatibile. */
    requestedPage: { type: Number, default: null },
    totalPages: { type: Number, default: 1 },
    totalItems: { type: Number, default: null },
    /* [sc-48337] L'API ferma il conteggio a 10.000 per non scansionare l'intera
     * tabella a ogni battuta. Quando è fermo, totale e numero di pagine sono un
     * minimo e vanno mostrati con il "+": scriverli come numeri esatti sarebbe
     * dire una cosa falsa. */
    totalIsCapped: { type: Boolean, default: false },
    showPagination: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    labelKey: {
      type: Function,
      default: (o) => o?.label ?? o?.name ?? o?.title ?? "",
    },
    valueKey: {
      type: Function,
      default: (o) => o?.id ?? o?.value ?? JSON.stringify(o),
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    emptyText: { type: String, default: "Nessun risultato" },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "Caricamento…" },
    /** legacy, resta supportato come fallback */
    initialQuery: { type: String, default: "" },
  },
  emits: [
    "update:modelValue",
    /** 👇 nuovo: consente v-model:text */
    "update:text",
    "search",
    "select",
    "action",
    "query-change",
    "cleared",
    /** 👇 nuovo: richiesta di cambio pagina dei risultati */
    "page-change",
  ],
  data() {
    return {
      open: false,
      /** 👇 priorità: prop `text` → `initialQuery` */
      query: this.text || this.initialQuery,
      activeIndex: -1,
      listboxId: `lb-${Math.random().toString(36).slice(2, 8)}`,
    };
  },
  computed: {
    activeId() {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    },
    /** Pagina "attiva" per la navigazione: quella richiesta (pending) se
     * presente, altrimenti quella caricata. */
    activePage() {
      return this.requestedPage != null ? this.requestedPage : this.currentPage;
    },
    // [sc-48337] Vedi la prop `totalIsCapped`.
    totalItemsLabel() {
      const n = Number(this.totalItems) || 0;
      return this.totalIsCapped
        ? `${n.toLocaleString("it-IT")}+`
        : n.toLocaleString("it-IT");
    },
    totalPagesLabel() {
      const n = Number(this.totalPages) || 1;
      return this.totalIsCapped ? `${n}+` : String(n);
    },
    /** Pagine da mostrare nel footer: sempre le prime due e le ultime due, più
     * una finestra di 3 pagine consecutive attorno alla pagina attiva (spostata
     * ai bordi così da mostrarne comunque tre: es. a pagina 1 → 1 2 3, in fondo
     * → penultime tre). Gli intervalli omessi diventano "…"; se il buco è di una
     * sola pagina si mostra il numero. Deriva solo da totalPages e activePage,
     * senza numeri fissi; nessun duplicato o valore fuori range. Chiavi stabili
     * anche per i separatori. */
    visiblePages() {
      const total = Number(this.totalPages) || 1;
      if (total <= 1) return [];
      const active = Math.min(Math.max(Number(this.activePage) || 1, 1), total);
      // Finestra di 3 pagine consecutive centrata sull'attiva, spostata ai bordi.
      let start = active - 1;
      let end = active + 1;
      if (start < 1) {
        end += 1 - start;
        start = 1;
      }
      if (end > total) {
        start -= end - total;
        end = total;
      }
      start = Math.max(1, start);
      end = Math.min(total, end);
      const wanted = new Set([1, 2, total - 1, total]);
      for (let p = start; p <= end; p += 1) wanted.add(p);
      const sorted = [...wanted]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);
      const out = [];
      let prev = 0;
      for (const p of sorted) {
        if (prev) {
          if (p - prev === 2) {
            out.push({
              type: "page",
              value: prev + 1,
              key: `page-${prev + 1}`,
            });
          } else if (p - prev > 2) {
            out.push({ type: "gap", key: `gap-${prev}-${p}` });
          }
        }
        out.push({ type: "page", value: p, key: `page-${p}` });
        prev = p;
      }
      return out;
    },
  },
  watch: {
    /** se cambia il testo controllato dal parent, aggiornalo */
    text(newText) {
      if ((newText || "") !== (this.query || "")) {
        this.query = newText || "";
      }
    },
    /** compat: se qualcuno usa ancora initialQuery */
    initialQuery(q) {
      if (!this.text) this.query = q || "";
    },
    /** quando il parent azzera il v-model, svuota anche il testo e chiudi */
    modelValue(v) {
      if (v === null || v === "" || typeof v === "undefined") {
        this.query = "";
        this.$emit("update:text", "");
        this.closeList();
        this.$emit("cleared");
      }
    },
    /** Caricata una nuova pagina, riporta l'elenco risultati in cima
     * mantenendo il dropdown aperto. */
    currentPage() {
      this.$nextTick(() => {
        if (this.$refs.listEl) this.$refs.listEl.scrollTop = 0;
      });
    },
  },
  mounted() {
    const st = window.getComputedStyle(this.$refs.inputEl);
    if (parseFloat(st.fontSize) < 16)
      this.$refs.inputEl.style.fontSize = "16px"; // iOS zoom fix
  },
  methods: {
    clear() {
      this.query = "";
      this.$emit("update:text", "");
      this.closeList();
      this.$emit("update:modelValue", null);
      this.$emit("cleared");
    },
    /** Emette il cambio pagina senza chiudere il dropdown né selezionare
     * un'opzione. Non emette mai pagine fuori range o uguali alla corrente. Un
     * click su una pagina diversa mentre è in corso un caricamento è ammesso e
     * "supera" la richiesta precedente (protezione lato pagina via request-id). */
    goToPage(p) {
      const page = Number(p);
      if (!Number.isInteger(page)) return;
      if (page < 1 || page > this.totalPages) return;
      // Nessun doppione verso la pagina già richiesta (pending) o caricata.
      if (page === this.activePage) return;
      this.$emit("page-change", page);
    },
    optionId(i) {
      return `${this.listboxId}-opt-${i}`;
    },
    openList() {
      if (this.disabled) return;
      const wasOpen = this.open;
      this.open = true;
      if (!wasOpen && !this.query) {
        this.$emit("search", "");
      }
      this.$nextTick(this.ensureVisible);
    },
    closeList() {
      this.open = false;
      this.activeIndex = -1;
    },
    onFocusIn() {},
    onFocusOut(e) {
      const goingTo = e.relatedTarget;
      if (!goingTo || !this.$el.contains(goingTo)) this.closeList();
    },
    onInput() {
      /** 👉 propaga SEMPRE il testo verso l’esterno */
      this.$emit("update:text", this.query);
      this.$emit("query-change", this.query);
      this.$emit("search", this.query?.trim() || "");
      this.openList();
      this.activeIndex = this.items.length ? 0 : -1;
    },
    onKeydown(e) {
      if (!this.open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        this.openList();
        e.preventDefault();
        this.activeIndex = this.items.length ? 0 : -1;
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          if (this.items.length) {
            this.activeIndex = (this.activeIndex + 1) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "ArrowUp":
          if (this.items.length) {
            this.activeIndex =
              (this.activeIndex - 1 + this.items.length) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "Enter":
          // Con la lista aperta l'Invio non deve propagarsi al form: senza
          // preventDefault scatta il submit implicito, che nel form preventivo
          // colpiva il primo bottone cestino e cancellava l'articolo aggiunto
          if (this.open) {
            if (this.activeIndex >= 0) this.choose(this.activeIndex);
            e.preventDefault();
          }
          break;
        case "Escape":
          this.closeList();
          e.preventDefault();
          break;
      }
    },
    setActive(i) {
      this.activeIndex = i;
    },
    choose(i) {
      const opt = this.items[i];
      if (!opt) return;
      const val = this.valueKey(opt, i);
      const lbl = this.labelKey(opt);
      this.$emit("update:modelValue", val);
      this.$emit("select", opt);
      /** 👉 imposta e propaga il testo “ufficiale” dell’opzione scelta */
      this.query = lbl;
      this.$emit("update:text", lbl);
      this.closeList();
      this.$nextTick(() => this.$refs.inputEl?.focus());
    },
    ensureVisible() {
      const list = this.$refs.listEl;
      if (!list || this.activeIndex < 0) return;
      const el = list.children[this.activeIndex];
      if (!el) return;
      const top = el.offsetTop,
        bottom = top + el.offsetHeight;
      const viewTop = list.scrollTop,
        viewBottom = viewTop + list.clientHeight;
      if (top < viewTop) list.scrollTop = top;
      else if (bottom > viewBottom) list.scrollTop = bottom - list.clientHeight;
    },
  },
};
</script>

<style scoped>
/* wrapper come il tuo .searchable-select globale, ma qui gestisco il posizionamento */
.searchable-select {
  position: relative;
  width: 100%;
}

/* [sc-48337] Pulsante "azzera". Sta nel componente e non in CreateQuotePage.css
   perché il combo è usato anche altrove, e legare il suo aspetto al foglio di
   stile di una pagina lo renderebbe invisibile nelle altre. */
.combo-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: #666;
  font-size: 13px;
  line-height: 1;
}

.combo-clear-btn:hover {
  color: #333;
}

/* Con il pulsante azione presente la "x" gli si mette accanto, non sopra. */
.searchable-select.with-action .combo-clear-btn {
  right: 42px;
}

.search-input {
  padding-right: 28px;
}

.searchable-select.with-action .search-input {
  padding-right: 64px;
}

.search-input {
  font-size: 12px !important;
  border: 1px solid #ccc;
  border-radius: 4px;
  line-height: 1.6;
  box-sizing: border-box;
}

/* allineo il dropdown allo stile esistente (.search-results del tuo CSS) ma lo rendo assoluto */
.search-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  z-index: 2147483647;
  -webkit-overflow-scrolling: touch;
}

/* rendi selezionabile con hover/attivo anche se sono <li> e non <option> */
.search-results .combo-option {
  padding: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-results .combo-option.highlighted,
.search-results .combo-option.is-active,
.search-results .combo-option:hover {
  background-color: #f5f5f5;
}

/* messaggi empty/loading */
.combo-empty,
.combo-loading {
  padding: 8px;
  font-size: 12px;
  color: #64748b;
}

/* Footer di paginazione: ancorato in fondo al dropdown, chiaramente distinto
   dalle opzioni articolo e non selezionabile. Controlli su più righe se
   necessario (flex-wrap), nessun overflow orizzontale. */
.combo-pagination {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: default;
}
.combo-pagination__info {
  font-size: 11px;
  color: #64748b;
}
.combo-pagination__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.combo-page-btn {
  min-width: 28px;
  padding: 4px 8px;
  font-size: 12px;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}
.combo-page-btn:hover:not(:disabled) {
  background: #eef2f7;
}
.combo-page-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.combo-page-num.is-current {
  background: #334155;
  color: #fff;
  border-color: #334155;
  font-weight: 600;
  opacity: 1;
}
/* Pagina richiesta ma non ancora caricata: distinta dalla corrente, non
   dichiarata come attiva/caricata. */
.combo-page-num.is-pending {
  border-color: #94a3b8;
  border-style: dashed;
  background: #eef2f7;
  color: #334155;
  font-style: italic;
  opacity: 1;
}
.combo-page-gap {
  padding: 0 2px;
  color: #94a3b8;
  font-size: 12px;
}
</style>
